import React from "react";
import { useRequest } from "ahooks";
import { checkOrder, logout, me } from "../api";
import Loader from "../components/loader/loader";
import { StatusConst, User, UserData, UserOrder } from "../interfaces";
import { toast } from "react-toastify";

export type Action =
  | { type: "SIGN_IN"; user: UserData }
  | { type: "UPDATE"; user?: UserData; order: UserOrder }
  | { type: "SIGN_OUT" };

export const defaultUser: User = {
  user: {
    id: "",
    email: "",
    name: "",
    isAdmin: false,
    isManufacturer: false,
  },
  order: {
    id: "",
    customerNumber: "",
    email: "",
    status: StatusConst.Waiting,
    billingAdress: {
      companyName: "",
      addressLine1: "",
      addressLine2: "",
      street: "",
      zipCode: "",
      city: "",
      country: "",
      phone: "",
    },
    shippingAdress: {
      companyName: "",
      addressLine1: "",
      addressLine2: "",
      street: "",
      zipCode: "",
      city: "",
      country: "",
      phone: "",
    },
    files: [],
    positions: [],
  },
};

const reducer = (state: any, action: Action) => {
  switch (action.type) {
    case "SIGN_IN":
      return {
        ...defaultUser,
        user: action.user,
        order: { ...defaultUser.order, id: "not_found" },
      };
    case "UPDATE":
      return {
        ...defaultUser,
        order: action.order,
        user: action.user ? action.user : defaultUser.user,
      };
    case "SIGN_OUT":
      logout();
      return {
        ...defaultUser,
        order: { ...defaultUser.order, id: "not_found" },
        user: { id: "guest", email: "", isAdmin: false },
      };
    default:
      return state;
  }
};

type Type = [User, (action: Action) => void];

const UserContext = React.createContext<Type>([defaultUser, () => {}]);

interface Props {
  children: React.ReactNode;
}

export const UserProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, defaultUser);

  /**
   * This method is used to get token from storage and login the customer user
   * @returns {UserOrder|boolean}
   */
  const getLoggedOrder = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const order = await checkOrder();
        if (!order) {
          toast.error("Session logout");
          dispatch({ type: "SIGN_OUT" });
          return false;
        }
        return order;
      } catch (err: any) {
        return false;
      }
    }
  };

  const { loading } = useRequest(me, {
    initialData: defaultUser.user,
    onSuccess: async (result: UserData) => {
      if (result.id) {
        dispatch({ type: "SIGN_IN", user: result });
      } else {
        const order = await getLoggedOrder();
        if (order) {
          dispatch({
            type: "UPDATE",
            order,
            user: {
              id: "guest",
              email: "",
              isAdmin: false,
              isManufacturer: false,
            },
          });
        } else {
          dispatch({
            type: "UPDATE",
            order: { ...defaultUser.order, id: "not_found" },
            user: {
              id: "guest",
              email: "",
              isAdmin: false,
              isManufacturer: false,
            },
          });
        }
      }
    },
  });

  return (
    <UserContext.Provider value={[state, dispatch]}>
      <Loader full loading={loading}>
        {children}
      </Loader>
    </UserContext.Provider>
  );
};

export const useUser = () => React.useContext<Type>(UserContext);
